import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type CreateSearchChipInput,
  LOCALES,
  type Locale,
  type SearchChipAdminItem,
} from '@bailafm/shared';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AlertTriangle, GripVertical, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { ChipDeleteDialog } from '@/components/chips/ChipDeleteDialog';
import { ChipFormDialog } from '@/components/chips/ChipFormDialog';
import { ApiClientError, chipsApi } from '@/lib/api';
import { cn } from '@/lib/utils';

// Cota dura de chips visibles en la home — coincide con la del API.
// Aquí solo afecta al copy informativo; el filtro real lo hace la API.
const PUBLIC_LIMIT = 7;

export default function ChipsPage() {
  const [chips, setChips] = useState<SearchChipAdminItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingChip, setEditingChip] = useState<SearchChipAdminItem | null>(null);
  const [chipToDelete, setChipToDelete] = useState<SearchChipAdminItem | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const items = await chipsApi.list();
      setChips(items);
    } catch (err) {
      setLoadError(
        err instanceof ApiClientError ? err.message : 'No se pudieron cargar los chips.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const activeChips = useMemo(
    () => chips.filter((c) => c.isActive).sort((a, b) => a.position - b.position),
    [chips],
  );
  const inactiveChips = useMemo(
    () => chips.filter((c) => !c.isActive).sort((a, b) => a.position - b.position),
    [chips],
  );
  const overLimit = activeChips.length > PUBLIC_LIMIT;

  async function handleCreateOrUpdate(values: CreateSearchChipInput, chip?: SearchChipAdminItem) {
    if (chip) {
      const updated = await chipsApi.update(chip.id, values);
      setChips((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    } else {
      const created = await chipsApi.create(values);
      setChips((prev) => [...prev, created]);
    }
  }

  async function handleDelete(chip: SearchChipAdminItem) {
    await chipsApi.delete(chip.id, chip._etag ?? '');
    setChips((prev) => prev.filter((c) => c.id !== chip.id));
  }

  async function handleToggleActive(chip: SearchChipAdminItem, next: boolean) {
    setChips((prev) =>
      prev.map((c) => (c.id === chip.id ? { ...c, isActive: next } : c)),
    );
    try {
      const updated = await chipsApi.update(chip.id, {
        isActive: next,
      });
      setChips((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    } catch (err) {
      setChips((prev) =>
        prev.map((c) => (c.id === chip.id ? { ...c, isActive: !next } : c)),
      );
      if (err instanceof ApiClientError && err.code === 'etag_mismatch') {
        await refresh();
      }
    }
  }

  // Solo se reordena el listado de activos. Los ocultos conservan su
  // posición original; al reactivarlos se reincorporan donde estaban.
  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = activeChips.findIndex((c) => c.id === active.id);
    const newIndex = activeChips.findIndex((c) => c.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reorderedActive = arrayMove(activeChips, oldIndex, newIndex).map(
      (c, i) => ({ ...c, position: i }),
    );

    // Optimista: pintamos el nuevo orden de activos junto a los ocultos
    // intactos (mantienen su position).
    setChips([...reorderedActive, ...inactiveChips]);

    try {
      const items = reorderedActive.map((c) => ({ id: c.id, position: c.position }));
      const updated = await chipsApi.reorder({ items });
      setChips(updated);
    } catch (err) {
      await refresh();
      setLoadError(
        err instanceof ApiClientError ? err.message : 'No se pudo reordenar.',
      );
    }
  }

  const sharedRowProps = (chip: SearchChipAdminItem) => ({
    chip,
    onToggleActive: (next: boolean) => void handleToggleActive(chip, next),
    onEdit: () => {
      setEditingChip(chip);
      setFormOpen(true);
    },
    onDelete: () => setChipToDelete(chip),
  });

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-3xl font-semibold tracking-tight">Chips del hero</h1>
          <p className="text-muted-foreground">
            Atajos de búsqueda que aparecen bajo el buscador del home. Mostramos
            los <strong>{PUBLIC_LIMIT}</strong> primeros activos por orden, solo
            en los idiomas con traducción.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingChip(null);
            setFormOpen(true);
          }}
        >
          <Plus className="size-4" aria-hidden /> Crear chip
        </Button>
      </header>

      {overLimit && (
        <p className="inline-flex items-center gap-1.5 rounded-md bg-amber-100 px-2 py-1 text-sm text-amber-900">
          <AlertTriangle className="size-4" aria-hidden />
          Hay más de {PUBLIC_LIMIT} chips activos: solo los {PUBLIC_LIMIT}
          {' '}primeros se mostrarán al público.
        </p>
      )}

      {loadError && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {loadError}
        </p>
      )}

      {loading && (
        <p className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
          Cargando…
        </p>
      )}

      {!loading && (
        <>
          <section className="space-y-3">
            <header className="flex items-baseline justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Activos
              </h2>
              <span className="text-xs text-muted-foreground">
                {activeChips.length} de {PUBLIC_LIMIT} visibles en la home
              </span>
            </header>

            {activeChips.length === 0 ? (
              <p className="rounded-lg border bg-card p-6 text-center text-sm text-muted-foreground">
                No hay chips activos. Crea uno o activa alguno de los ocultos.
              </p>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={activeChips.map((c) => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <ul className="space-y-2">
                    {activeChips.map((chip) => (
                      <SortableChipRow key={chip.id} {...sharedRowProps(chip)} />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}
          </section>

          {inactiveChips.length > 0 && (
            <section className="space-y-3">
              <header className="flex items-baseline justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Ocultos
                </h2>
                <span className="text-xs text-muted-foreground">
                  {inactiveChips.length} sin publicar
                </span>
              </header>
              <ul className="space-y-2">
                {inactiveChips.map((chip) => (
                  <li key={chip.id}>
                    <ChipRowBody {...sharedRowProps(chip)} />
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      <ChipFormDialog
        open={formOpen}
        chip={editingChip ?? undefined}
        onOpenChange={setFormOpen}
        onSubmit={async (values, chip) => {
          await handleCreateOrUpdate(values, chip);
        }}
      />

      <ChipDeleteDialog
        chip={chipToDelete}
        onClose={() => setChipToDelete(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}

interface ChipRowBodyProps {
  chip: SearchChipAdminItem;
  onToggleActive(next: boolean): void;
  onEdit(): void;
  onDelete(): void;
  // Si se pasa, se pinta a la izquierda como handle de drag. Si no,
  // la fila no es arrastrable (caso de ocultos).
  dragHandle?: React.ReactNode;
}

function ChipRowBody({ chip, onToggleActive, onEdit, onDelete, dragHandle }: ChipRowBodyProps) {
  // El "preview" del label usa la primera traducción presente, en el
  // orden es → ca → en. Es solo para identificar la fila visualmente.
  const preview =
    chip.label.es ?? chip.label.ca ?? chip.label.en ?? '(sin traducción)';

  return (
    <article
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3',
        !chip.isActive && 'opacity-70',
      )}
    >
      {dragHandle}

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium">{preview}</span>
          <LocalesBadges label={chip.label} />
        </div>
        <code className="truncate text-xs text-muted-foreground">
          {chip.url.es}
          {urlLocaleSuffix(chip.url)}
        </code>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">
          {chip.isActive ? 'Activo' : 'Oculto'}
        </span>
        <Switch
          checked={chip.isActive}
          onCheckedChange={onToggleActive}
          aria-label="Activar chip"
        />
      </div>

      <div className="flex items-center gap-1">
        <Button type="button" variant="ghost" size="icon" aria-label="Editar" onClick={onEdit}>
          <Pencil className="size-4" aria-hidden />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Eliminar"
          onClick={onDelete}
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="size-4" aria-hidden />
        </Button>
      </div>
    </article>
  );
}

function SortableChipRow(props: Omit<ChipRowBodyProps, 'dragHandle'>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: props.chip.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handle = (
    <button
      type="button"
      aria-label="Arrastra para reordenar"
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground',
        'cursor-grab touch-none hover:bg-muted hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isDragging && 'cursor-grabbing',
      )}
      {...attributes}
      {...listeners}
    >
      <GripVertical className="size-4" aria-hidden />
    </button>
  );

  return (
    <li ref={setNodeRef} style={style} className={cn(isDragging && 'z-10')}>
      <div className={cn(isDragging && 'shadow-lg ring-2 ring-foreground/20 rounded-lg')}>
        <ChipRowBody {...props} dragHandle={handle} />
      </div>
    </li>
  );
}

// Resume las URLs por idioma extra (ca/en) que difieren de la de `es`,
// para indicar a simple vista que el chip tiene destinos traducidos.
function urlLocaleSuffix(url: SearchChipAdminItem['url']): string {
  const extra = (['ca', 'en'] as const).filter((l) => url[l] && url[l] !== url.es);
  return extra.length > 0 ? `  (+${extra.join(', ')})` : '';
}

function LocalesBadges({ label }: { label: SearchChipAdminItem['label'] }) {
  return (
    <span className="flex items-center gap-1">
      {LOCALES.map((l) => {
        const present = typeof label[l as Locale] === 'string';
        return (
          <span
            key={l}
            className={cn(
              'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
              present
                ? 'bg-foreground text-background'
                : 'bg-muted text-muted-foreground/60',
            )}
            aria-label={`${l} ${present ? 'presente' : 'ausente'}`}
          >
            {l}
          </span>
        );
      })}
    </span>
  );
}
